/*
 * The dock's imperative surface: a module singleton `lib/actions.js` can call. Binds to the live dockview api and the registry at mount, so this file imports no panel component and sits in no import cycle.
 */
import { flashPanel } from '~/lib/flashPanel'
import store from '~/store'
import { selectEffectiveShowOutput } from '~/store/app/derived'
import { toggleOutput } from '~/store/app/thunks'
import { resetDockLayout, selectDockPlacements, setDockPlacement, setUIProperty } from '~/store/ui'
import { selectShow } from '~/store/ui/selectors'
import { expandGroup } from './collapse'
import { MONITOR_IDS } from './ids'

const logger = console.scope('dock')

let api = null
let registry = null

const getState = () => store.getState()

// `null` on unmount, so a late call finds no dock rather than a disposed one.
export function bindDockApi(nextApi, nextRegistry = registry) {
	api = nextApi
	registry = nextRegistry
}

export const getDockApi = () => api

// Performance mode locks the dock; a panel added there would sit outside the snapshot its exit restores.
const isPerformanceMode = () => {
	const show = selectShow(getState())
	return show.minimal || !show.ui
}

const DIRECTION = { left: 'left', mid: 'left', right: 'right', monitor: 'below', center: 'within' }

const flip = o => (o === 'HORIZONTAL' ? 'VERTICAL' : 'HORIZONTAL')
const firstLeaf = node => (node.type === 'leaf' ? node : firstLeaf(node.data[0]))

/* Where a group sits in the serialized grid: its parent branch's orientation, its index there and its neighbours. */
function locateGroup(grid, groupId) {
	const walk = (node, orientation) => {
		if (node.type !== 'branch') return null
		const i = node.data.findIndex(n => n.type === 'leaf' && n.data.id === groupId)
		if (i >= 0) return { branch: node, index: i, orientation, root: node === grid.root }
		for (const child of node.data) {
			const hit = walk(child, flip(orientation))
			if (hit) return hit
		}
		return null
	}
	return walk(grid.root, grid.orientation)
}

const describePlacement = panel => {
	const siblings = panel.group.panels.filter(p => p !== panel).map(p => p.id)
	return siblings.length ? { siblings } : { siblings, ...describeSlot(panel.group) }
}

// ⚠️ No group id: dockview renumbers groups after a reload or rebuild, so a remembered id names an unrelated group.
function describeSlot(group) {
	const placement = {}
	const grid = api.toJSON().grid
	const at = locateGroup(grid, group.id)
	if (!at) return placement
	const { branch, index, orientation, root } = at
	const horizontal = orientation === 'HORIZONTAL'
	placement.size = branch.data[index].size
	placement.axis = horizontal ? 'width' : 'height'
	const last = index === branch.data.length - 1
	if (root && (index === 0 || last)) {
		placement.edge = horizontal ? (index === 0 ? 'left' : 'right') : index === 0 ? 'top' : 'bottom'
		return placement
	}
	const after = branch.data[index + 1]
	const neighbour = after ?? branch.data[index - 1]
	const leaf = firstLeaf(neighbour)
	placement.referencePanel = leaf.data.activeView ?? leaf.data.views[0]
	placement.direction = horizontal ? (after ? 'left' : 'right') : after ? 'above' : 'below'
	return placement
}

export function resolveAnchor(id) {
	if (!api) return
	const anchor = registry.PANELS[id]?.anchor

	const placements = selectDockPlacements(getState())
	const placement = placements[id]
	if (placement) {
		for (const sid of placement.siblings ?? []) {
			const p = api.getPanel(sid)
			if (p) return { position: { referenceGroup: p.group, direction: 'within' } }
		}
		// The group is gone: this panel's slot, or the slot the last tab-mate to leave recorded.
		const slots = [placement, ...(placement.siblings ?? []).map(sid => placements[sid]).filter(Boolean)]
		for (const slot of slots) {
			const size =
				slot.size && slot.axis ? { [slot.axis === 'width' ? 'initialWidth' : 'initialHeight']: slot.size } : {}
			if (slot.edge) return { position: { direction: slot.edge }, ...size }
			if (slot.referencePanel && api.getPanel(slot.referencePanel)) {
				return { position: { referencePanel: slot.referencePanel, direction: slot.direction }, ...size }
			}
		}
	}

	const sibling = api.panels.find(p => p.id !== id && registry.PANELS[p.id]?.anchor === anchor)
	if (sibling) return { position: { referenceGroup: sibling.group, direction: 'within' } }

	if (api.getPanel('stage')) {
		return { position: { referencePanel: 'stage', direction: DIRECTION[anchor] ?? 'right' } }
	}
	return {}
}

export function openPanel(id) {
	return isPerformanceMode() ? null : placePanel(id)
}

// Ungated: hydration and `conformPanels` run in performance mode too.
export function placePanel(id) {
	if (!api) return null
	const existing = api.getPanel(id)
	if (existing) {
		expandGroup(api, existing.group)
		existing.api.setActive()
		return existing
	}
	const def = registry.PANELS[id]
	if (!def || !registry.isPanelAllowed(id, getState())) return null
	return api.addPanel({
		id,
		component: id,
		title: def.title,
		renderer: 'always',
		...registry.constraintsOf(id),
		...resolveAnchor(id),
	})
}

export function closePanel(id) {
	const panel = api?.getPanel(id)
	if (!panel) return
	if (MONITOR_IDS.includes(id)) {
		hideMonitor(id)
		return
	}
	if (registry.PANELS[id]?.closable === false) {
		logger.warn('closePanel', `${id} is not closable`)
		return
	}
	store.dispatch(setDockPlacement({ id, placement: describePlacement(panel) }))
	api.removePanel(panel)
}

/* Closes every closable panel in the group and hides its monitors. Each closed tab records all its tab-mates and the group's slot, so reopening them in any order rebuilds one group there. */
export function closeGroup(group) {
	// ⚠️ A copy: `removePanel` mutates `group.panels`, and dockview disposes the group with its last panel.
	const panels = [...group.panels]
	const slot = describeSlot(group)
	for (const panel of panels) {
		const { id } = panel
		if (MONITOR_IDS.includes(id)) hideMonitor(id)
		else if (registry.PANELS[id]?.closable !== false) {
			const siblings = panels.filter(p => p !== panel).map(p => p.id)
			store.dispatch(setDockPlacement({ id, placement: { siblings, ...slot } }))
			api.removePanel(panel)
		}
	}
}

let keyboardCloseHeld = false

/* dockview closes a focused tab on Delete and Backspace, the keys the app binds to deleting the selection. The dock's capture-phase keydown calls this; the hold outlasts that event's dispatch. */
export function holdKeyboardClose() {
	keyboardCloseHeld = true
	setTimeout(() => {
		keyboardCloseHeld = false
	})
}

// dockview's own closes (tab menu, Delete on a tab) call `panel.api.close`; `removePanel` does not, so this cannot recurse.
export function routeClose(panel) {
	panel.api.close = () => {
		if (!keyboardCloseHeld) closePanel(panel.id)
	}
}

// ⚠️ A monitor stays in the layout: removing the stage would unmount the pixi pipeline. Closing turns its flag off, which hides its tab and, when nothing else shows, its group.
function hideMonitor(id) {
	const state = getState()
	if (id === 'stage' && selectShow(state).source) store.dispatch(setUIProperty(['show.source', false]))
	if (id === 'output' && selectEffectiveShowOutput(state)) store.dispatch(toggleOutput())
}

/* Opens every panel the mode requires (the monitors, each allowed non-closable panel) and removes each one it disallows. Opens first, so a required panel can join the group of the one it replaces. */
export function conformPanels() {
	if (!api) return
	const state = getState()
	const { PANELS, isPanelAllowed } = registry
	for (const id of Object.keys(PANELS)) {
		const required = MONITOR_IDS.includes(id) || PANELS[id].closable === false
		if (required && isPanelAllowed(id, state) && !api.getPanel(id)) placePanel(id)
	}
	for (const panel of [...api.panels]) {
		if (isPanelAllowed(panel.id, state)) continue
		store.dispatch(setDockPlacement({ id: panel.id, placement: describePlacement(panel) }))
		api.removePanel(panel)
	}
}

export function togglePanel(id) {
	if (!api || isPerformanceMode()) return
	if (api.getPanel(id)) closePanel(id)
	else openPanel(id)
}

export function revealPanel(id, focusSelector) {
	if (isPerformanceMode()) return
	const panel = openPanel(id)
	if (!panel) return
	panel.api.setActive()
	requestAnimationFrame(() => {
		flashPanel(id)
		if (focusSelector) document.querySelector(focusSelector)?.focus()
	})
}

// ⚠️ Never `api.clear()`: removing the stage unmounts the pixi pipeline. Every other panel goes and the default layout grows around the stage, docked back into the grid if it floated.
export function resetLayout() {
	if (!api || isPerformanceMode()) return
	store.dispatch(resetDockLayout())
	const stage = api.getPanel('stage')
	for (const panel of [...api.panels]) if (panel !== stage) api.removePanel(panel)
	if (stage && stage.api.location.type !== 'grid') stage.api.moveTo({ group: api.addGroup(), position: 'center' })
	registry.buildDefaultLayout(api, getState())
}
